<script setup>
import { onMounted, onUnmounted, ref, watch, nextTick, computed } from "vue";
import interactjs from "interactjs";
import debounce from "lodash.debounce";
import { getImageInfo } from "../utils/preload-image";
import { SURFACES } from "../misc/constants";

const emit = defineEmits(["resize", "clearArtworkSize", "initSize"]);
const props = defineProps({
  artworkSize: {
    type: Object,
    required: true
  },
  oneInch: {
    type: Number,
    required: true
  },
  surfaceLabel: {
    type: String,
    required: true
  },
  artworkUrl: {
    type: String,
    required: true
  },
  surface: {
    type: Object,
    required: true
  }
});

const style = ref(null);
const lastImageCache = ref({ cachedPicture: undefined, cachedImageInfo: undefined });
const designReferenceHandler = ref(null);
const designReference = ref(null);
const interact = ref(null);
const widthMeasurement = ref('0"');
const heightMeasurement = ref('0"');

const setSize = size => {
  if (size.width && size.height) {
    style.value = {
      width: `${size.width}px`,
      height: `${size.height}px`
    };
    widthMeasurement.value = formatMeasurement(size.width);
    heightMeasurement.value = formatMeasurement(size.height);
  } else {
    style.value = null;
    widthMeasurement.value = '0"';
    heightMeasurement.value = '0"';
  }
};

const updateSize = () => {
  const rect = interact.value.getRect(designReference.value);
  emit("resize", { width: rect.width, height: rect.height });
};

const initSize = (resetSize = false) => {
  if (!resetSize && props.artworkSize.width && props.artworkSize.height) {
    setSize(props.artworkSize);
    return;
  }
  const rect = interact.value.getRect(designReference.value);
  emit("initSize", { width: rect.width, height: rect.height });
};

const formatMeasurement = pixels => {
  const inches = pixels / props.oneInch;
  return `${inches.toFixed(2)}"`;
};

// eslint-disable-next-line max-lines-per-function
const initDesign = async (resetSize = false) => {
  try {
    const { cachedPicture, cachedImageInfo } = lastImageCache.value;
    let ratio;

    if (cachedPicture === props.artworkUrl && props.surfaceLabel === SURFACES.BACK) {
      ratio = cachedImageInfo.ratio;
    } else {
      const info = await getImageInfo(props.artworkUrl);
      ratio = info.ratio;
      lastImageCache.value = { cachedPicture: props.artworkUrl, cachedImageInfo: info };
    }

    if (!ratio) {
      throw new Error("Failed to calculate image ratio");
    }

    const minimalResizeImage =
      ratio > 1
        ? { width: props.oneInch * ratio, height: props.oneInch }
        : { width: props.oneInch, height: props.oneInch / ratio };

    interact.value = interactjs(designReferenceHandler.value).resizable({
      edges: { left: true, right: true, bottom: true, top: true },
      listeners: {
        move(event) {
          const { width, height } = event.rect;
          style.value = { width: `${width}px`, height: `${height}px` };
          // Update measurements in real-time
          widthMeasurement.value = formatMeasurement(width);
          heightMeasurement.value = formatMeasurement(height);
        },
        end: updateSize
      },
      restrictEdges: { outer: "parent", endOnly: true },
      modifiers: [
        interactjs.modifiers.aspectRatio({
          ratio,
          modifiers: [interactjs.modifiers.restrictSize({ max: "parent" })]
        }),
        interactjs.modifiers.restrictSize({ min: minimalResizeImage })
      ]
    });
    initSize(resetSize);
  } catch (error) {
    emit("clearArtworkSize");
  }
};

let prevInnerWidth;
const resizeDesign = debounce(() => {
  const currentInnerWidth = window.innerWidth;
  if (currentInnerWidth === prevInnerWidth) return;
  emit("clearArtworkSize");
  nextTick(initDesign);
  prevInnerWidth = currentInnerWidth;
}, 1500);

watch(
  () => [props.artworkUrl, props.surfaceLabel],
  async ([newUrl], [oldUrl]) => {
    emit("clearArtworkSize");
    nextTick(initDesign);
  }
);

const showMeasurementLabel = computed(
  () => widthMeasurement.value !== '0"' && heightMeasurement.value !== '0"'
);

watch(() => props.artworkSize, setSize);

watch(
  () => props.oneInch,
  async (newVal, oldVal) => {
    if (newVal === oldVal) return;

    emit("clearArtworkSize");
    await initDesign(true);
  }
);

onMounted(() => {
  initDesign();
  window.addEventListener("resize", resizeDesign);
});

onUnmounted(() => {
  if (interact.value) {
    interact.value.unset();
    interact.value = null;
  }
  window.removeEventListener("resize", resizeDesign);
});
</script>

<template>
  <img
    ref="designReference"
    data-testid="artwork-design-img"
    class="artwork-design-img"
    :src="props.artworkUrl"
    :style="style"
    :alt="`Artwork preview for ${props.surfaceLabel}`"
  />
  <div
    ref="designReferenceHandler"
    class="artwork-design-border"
    :style="style"
    role="application"
    aria-label="Resizable artwork border"
    tabindex="0"
  >
    <template v-if="showMeasurementLabel">
      <div
        class="artwork-design-measurement artwork-design-measurement--left"
        :class="{ 'artwork-design-measurement--dark': props.surface.is_dark }"
        role="status"
        :aria-label="`Artwork height: ${heightMeasurement}`"
        tabindex="0"
      >
        <span class="artwork-design-measurement__label">
          {{ heightMeasurement }}
        </span>
      </div>
      <div
        class="artwork-design-measurement artwork-design-measurement--bottom"
        :class="{ 'artwork-design-measurement--dark': props.surface.is_dark }"
        role="status"
        :aria-label="`Artwork width: ${widthMeasurement}`"
        tabindex="0"
      >
        <span class="artwork-design-measurement__label">
          {{ widthMeasurement }}
        </span>
      </div>
    </template>
    <div
      class="artwork-design-border__handle artwork-design-border__handle--left"
      role="button"
      aria-label="Resize from left"
      tabindex="0"
    />
    <div
      class="artwork-design-border__handle artwork-design-border__handle--right"
      role="button"
      aria-label="Resize from right"
      tabindex="0"
    />
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.artwork-design-img {
  display: block;
  user-select: none;
  margin: 0 auto;
  touch-action: none;
  max-width: 100%;
  max-height: 100%;
  pointer-events: none;
}

.artwork-design-border {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  outline: 2px solid $ultramarine-blue;
  touch-action: none;
  pointer-events: all;

  &::before {
    position: absolute;
    inset: 0;
    margin: -20px;
    content: "";

    @include with-desktop {
      display: none;
    }
  }

  &__handle {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid $ultramarine-blue;
    background-color: $white;
    bottom: -11px;
    cursor: inherit;

    &--left {
      left: -11px;
    }

    &--right {
      right: -11px;
    }
  }
}

.artwork-design-measurement {
  position: absolute;
  color: $pod-gray-modern-900;
  background: currentColor;
  border-radius: 5px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 700;
  pointer-events: none;
  user-select: none;

  &--dark {
    color: $white;
  }

  &--left {
    left: 0;
    top: 50%;
    transform: translate(-110%, -50%);
  }

  &--bottom {
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 125%);
  }

  &__label {
    filter: invert(1);
  }
}
</style>
